import {
  Building2,
  ChevronDown,
  LogOut,
  Menu,
  Settings,
  User,
} from "@ews-admin/global-design-system";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../store/hooks";
import { requestTerminateSessionRequest } from "../../store/reducers/sessionSlice";
import { roleLabel } from "../../utils/enum-label-utils";
import { NotificationsPanel } from "./NotificationsPanel";

interface TopNavigationProps {
  onMenuClick?: () => void;
}

export const TopNavigation: React.FC<TopNavigationProps> = ({
  onMenuClick,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // MED-1514: the header used to print a hardcoded "Admin User" /
  // "Administrator" and a stock stranger's photo for every account. The
  // logged-in administrator is already in the session — the same
  // `state.session.session?.user` the profile page reads — so name them.
  const user = useAppSelector(state => state.session.session?.user);
  const [showUserMenu, setShowUserMenu] = useState(false);

  const accountName =
    [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim() ||
    t("admin.header.account");
  // Empty when the session carries no role — better a name on its own than the
  // name repeated underneath it.
  const accountRole = roleLabel(t, user?.roleName);

  // Refs for click outside detection
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Handle click outside for user menu dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node)
      ) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showUserMenu]);

  const goTo = (path: string) => {
    setShowUserMenu(false);
    navigate(path);
  };

  const handleLogout = () => {
    // Close user menu
    setShowUserMenu(false);

    // Dispatch terminate session action - saga handles API call and redirect
    dispatch(requestTerminateSessionRequest());
  };

  return (
    <header className="px-4 py-3 bg-white border-b border-neutral-200">
      <div className="flex justify-between items-center">
        {/* Left side - Logo and Mobile Menu */}
        <div className="flex items-center space-x-4">
          {/* Mobile Menu Button */}
          <button
            onClick={onMenuClick}
            className="p-2 rounded-lg transition-colors lg:hidden hover:bg-neutral-100"
          >
            <Menu className="w-5 h-5 text-neutral-600" />
          </button>

          {/* Logo - Hidden on mobile when sidebar is open */}
          <div className="flex items-center space-x-3">
            <div className="flex justify-center items-center w-8 h-8 bg-gradient-to-br rounded-lg from-ews-primary to-secondary-500">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div className="hidden sm:block">
              <h1 className="text-lg font-semibold text-neutral-900">
                {t("admin.header.title")}
              </h1>
              <p className="text-xs text-neutral-500">
                {t("admin.header.subtitle")}
              </p>
            </div>
          </div>
        </div>

        {/* Right side - Notifications and User Menu */}
        {/*
          MED-1447: the bell is now backed by the BFF notifications API
          (list / mark read / mark all read / delete) — it used to open a panel
          of hardcoded demo rows and issue no request at all.
          MED-1448 / MED-1449: the "Profile" and "Settings" entries point at
          the admin's own /admin/profile and /admin/settings routes. They used
          to target /manager/*, which belongs to the dashboard app and 404s
          here; nothing in this header may navigate to a /manager/ path.
        */}
        <div className="flex items-center space-x-4">
          <NotificationsPanel />
          {/* User Menu */}
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              aria-label={accountName}
              aria-haspopup="true"
              aria-expanded={showUserMenu}
              className="flex items-center p-2 space-x-3 rounded-lg transition-colors hover:bg-neutral-100"
            >
              {/* No stock photo stands in for a missing one: an account with
                  no `photoUrl` gets the same neutral placeholder the profile
                  page uses, so nothing points at a dead image. */}
              {user?.photoUrl ? (
                <img
                  src={user.photoUrl}
                  alt={accountName}
                  className="object-cover w-8 h-8 rounded-full"
                />
              ) : (
                <span className="flex justify-center items-center w-8 h-8 rounded-full bg-neutral-100">
                  <User className="w-4 h-4 text-neutral-400" />
                </span>
              )}
              <div className="hidden text-left sm:block">
                <p className="text-sm font-medium text-neutral-900">
                  {accountName}
                </p>
                {accountRole && (
                  <p className="text-xs text-neutral-500">{accountRole}</p>
                )}
              </div>
              <ChevronDown className="w-4 h-4 text-neutral-600" />
            </button>

            {/* User Menu Dropdown */}
            {showUserMenu && (
              <div className="absolute right-0 z-50 mt-2 w-48 bg-white rounded-xl border shadow-soft border-neutral-200">
                <div className="py-1">
                  <button
                    onClick={() => goTo("/admin/profile")}
                    className="flex items-center px-4 py-2 space-x-3 w-full text-sm transition-colors duration-200 text-neutral-700 hover:bg-neutral-50"
                  >
                    <User className="w-4 h-4" />
                    <span>{t("common.profile")}</span>
                  </button>
                  <button
                    onClick={() => goTo("/admin/settings")}
                    className="flex items-center px-4 py-2 space-x-3 w-full text-sm transition-colors duration-200 text-neutral-700 hover:bg-neutral-50"
                  >
                    <Settings className="w-4 h-4" />
                    <span>{t("common.settings")}</span>
                  </button>
                  <div className="my-1 border-t border-neutral-200" />
                  <button
                    onClick={handleLogout}
                    className="flex items-center px-4 py-2 space-x-3 w-full text-sm transition-colors duration-200 text-error-600 hover:bg-error-50"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>{t("common.logout")}</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};
